import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { trpc } from '@web-app/utils/trpc';
import { forwardRef, useState } from 'react';
import { FiShield } from 'react-icons/fi';
import type { Value as E164Number } from 'react-phone-number-input';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input/input';

const ChakraPhoneInput = forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => <Input ref={ref} {...props} autoFocus />,
);
ChakraPhoneInput.displayName = 'ChakraPhoneInput';

export const MfaPhoneModal = () => {
  const toast = useToast();
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const { data, isLoading } =
    trpc.providerProfile.getPhoneVerificationStatus.useQuery();

  const utils = trpc.useUtils();

  const updateMutation = trpc.providerProfile.updatePhoneNumber.useMutation({
    onSuccess: () => {
      utils.providerProfile.getPhoneVerificationStatus.invalidate();
      toast({
        title: 'Phone number saved and confirmed',
        status: 'success',
        duration: 3000,
      });
      onClose();
    },
  });

  const verifyMutation = trpc.providerProfile.verifyPhoneNumber.useMutation({
    onSuccess: () => {
      utils.providerProfile.getPhoneVerificationStatus.invalidate();
      toast({
        title: 'Phone number confirmed',
        status: 'success',
        duration: 3000,
      });
      onClose();
    },
  });

  const [phoneNumber, setPhoneNumber] = useState<E164Number | undefined>();
  const [hasEditedNumber, setHasEditedNumber] = useState(false);

  // Don't render if loading, no data, or already verified
  if (isLoading || !data || data.isVerified) {
    return null;
  }

  const hasExistingNumber = !!data.phoneNumber;
  const displayValue = hasEditedNumber
    ? phoneNumber
    : ((data.phoneNumber as E164Number | undefined) ?? phoneNumber);
  const isValidPhone = displayValue ? isValidPhoneNumber(displayValue) : false;
  const isMutating = updateMutation.isPending || verifyMutation.isPending;
  const mutationError =
    updateMutation.error?.message || verifyMutation.error?.message;

  const handlePhoneChange = (value: E164Number | undefined) => {
    setPhoneNumber(value);
    if (!hasEditedNumber && value !== data.phoneNumber) {
      setHasEditedNumber(true);
    }
  };

  const handleSubmit = () => {
    if (isMutating) return;

    // If they haven't edited and there's an existing number, just verify
    if (!hasEditedNumber && hasExistingNumber) {
      verifyMutation.mutate();
      return;
    }

    // Otherwise, update with the new number
    if (displayValue && isValidPhone) {
      updateMutation.mutate({ phoneNumber: displayValue });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValidPhone && !isMutating) {
      handleSubmit();
    }
  };

  const canSubmit = hasExistingNumber && !hasEditedNumber ? true : isValidPhone;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="md"
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent
        bg="surface.default"
        borderColor="border.default"
        borderWidth={1}
        borderRadius={10}
        color="text.default"
      >
        <ModalHeader display="flex" alignItems="center" gap={2} mt={2}>
          <FiShield />
          Action Required by March 24
        </ModalHeader>

        <ModalBody as={VStack} spacing={4} alignItems="stretch">
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm" fontWeight="semibold">
              Starting March 24, Healthie will enforce mandatory text
              verification. You will be locked out if your phone number is not
              confirmed before then.
            </Text>
          </Alert>

          <Text color="text.muted" fontSize="sm">
            {hasExistingNumber
              ? 'Please confirm the number below is correct, or update it, to ensure you don\u2019t lose access to patient records and scheduling on March 24.'
              : 'Please provide your mobile number now to ensure you don\u2019t lose access to patient records and scheduling on March 24.'}
          </Text>

          {mutationError && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {mutationError}
            </Alert>
          )}

          <FormControl>
            <FormLabel fontSize="sm">Mobile phone number</FormLabel>
            <PhoneInput
              country="US"
              value={displayValue}
              onChange={handlePhoneChange}
              onKeyDown={handleKeyDown}
              inputComponent={ChakraPhoneInput}
              disabled={isMutating}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter justifyContent="flex-end" gap={3} mb={4}>
          <Button variant="outline" onClick={onClose}>
            Remind Me Next Time
          </Button>
          <Button
            bg="text.brand"
            color="white"
            onClick={handleSubmit}
            isDisabled={!canSubmit || isMutating}
            isLoading={isMutating}
            leftIcon={<FiShield />}
            _hover={{ bg: 'green.600' }}
          >
            {hasExistingNumber && !hasEditedNumber
              ? 'Confirm This Number'
              : 'Save & Confirm'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
